/**
 * API client for the Customs Search backend.
 */

// Auto-detect API URL.
// - localhost dev: backend on :8000
// - production (через CF Tunnel / reverse proxy): same origin, /api/* проксируется на бэк
// - REACT_APP_API_URL переопределяет всё (для нестандартных деплоев)
function getBaseUrl() {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, origin } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  return origin;
}
const BASE_URL = getBaseUrl();

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, params = {}) {
  const url = new URL(path, BASE_URL);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.append(key, value);
    }
  });

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch(url.toString(), {
      signal: controller.signal,
      headers: { 'Accept': 'application/json' },
    });

    if (!response.ok) {
      const text = await response.text();
      let message = text || response.statusText;
      try {
        const parsed = JSON.parse(text);
        message = parsed?.detail || message;
      } catch {
        // Keep the raw backend text when it is not JSON.
      }
      throw new ApiError(message, response.status);
    }

    return await response.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      throw new ApiError('Запрос превысил таймаут (30с)', 408);
    }
    throw err;
  } finally {
    clearTimeout(timeout);
  }
}

// ── Search ────────────────────────────────────────

export async function search(query, mode = 'auto', filters = {}, limit = 50, offset = 0, sort = null, sortOrder = 'desc') {
  return request('/api/v2/search', {
    q: query,
    mode,
    limit,
    offset,
    sort,
    sort_order: sortOrder,
    year_from: filters.yearFrom,
    year_to: filters.yearTo,
    country: filters.country,
    trading_country: filters.tradingCountry,
    value_min: filters.valueMin,
    value_max: filters.valueMax,
    weight_min: filters.weightMin,
    weight_max: filters.weightMax,
    trademark: filters.trademark,
    importer: filters.importer,
    exporter: filters.exporter,
    product_code_prefix: filters.productCodePrefix,
  });
}

// ── Declarations ──────────────────────────────────

export async function getDeclaration(id) {
  return request(`/api/v2/declarations/${id}`);
}

export async function getSimilarProducts(id, limit = 20) {
  return request(`/api/v2/declarations/${id}/similar`, { limit });
}

// ── Codes ─────────────────────────────────────────

export async function suggestCodes(prefix, limit = 20) {
  return request('/api/v2/codes/suggest', { prefix, limit });
}

export async function getCodeStats(code) {
  return request(`/api/v2/codes/${code}/stats`);
}

export async function getCodeBrands(code, limit = 30) {
  return request(`/api/v2/codes/${code}/brands`, { limit });
}

export async function getCodeExamples(code, limit = 30) {
  return request(`/api/v2/codes/${code}/examples`, { limit });
}

// ── Filters metadata ──────────────────────────────

export async function getCountries(limit = 100) {
  return request('/api/v2/filters/countries', { limit });
}

export async function getYears() {
  return request('/api/v2/filters/years');
}

export async function suggestTrademarks(q, limit = 20) {
  return request('/api/v2/filters/trademarks', { q, limit });
}

export async function suggestImporters(q, limit = 20) {
  return request('/api/v2/filters/importers', { q, limit });
}

// ── Tariff tree ──────────────────────────────────

export async function getTariffTree(code) {
  return request(`/api/v2/tariff/${encodeURIComponent(code)}`);
}

export async function getTariffByNodeId(hsId) {
  return request(`/api/v2/tariff/node/${hsId}`);
}

export async function getTariffChildren(code) {
  return request(`/api/v2/tariff/${encodeURIComponent(code)}/children`);
}

export async function getFullTariffTree() {
  return request('/api/v2/tariff/tree/full');
}

export async function getTariffRates(code) {
  return request(`/api/v2/tariff/${encodeURIComponent(code)}/rates`);
}

export async function getAnalyticsCard(code, { date, ccdType = 101, originCountry } = {}) {
  return request(`/api/v2/tariff/${encodeURIComponent(code)}/analytics-card`, {
    date,
    ccd_type: ccdType,
    origin_country: originCountry,
  });
}

export async function getAnalyticsNode(code, nodeId, { date, ccdType = 101, originCountry } = {}) {
  return request(`/api/v2/tariff/${encodeURIComponent(code)}/analytics-node`, {
    node_id: nodeId,
    date,
    ccd_type: ccdType,
    origin_country: originCountry,
  });
}

export async function getAnalyticsLawText(textPath, textFile) {
  return request('/api/v2/tariff/analytics/law-text', {
    text_path: textPath,
    text_file: textFile,
  });
}

export function getAnalyticsReportUrl(code, { date, ccdType = 101, originCountry } = {}) {
  const url = new URL(`/api/v2/tariff/${encodeURIComponent(code)}/analytics-report`, BASE_URL);
  const params = { date, ccd_type: ccdType, origin_country: originCountry };
  Object.entries(params).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      url.searchParams.append(key, value);
    }
  });
  return url.toString();
}

// ── System ────────────────────────────────────────

export async function healthCheck() {
  return request('/api/v2/health');
}

export async function getInfo() {
  return request('/api/v2/info');
}

// ── Analytics ─────────────────────────────────────

export async function analyticsPriceDistribution({ code, country, dateFrom, dateTo, bins }) {
  return request('/api/v2/analytics/price_distribution', {
    code, country,
    date_from: dateFrom, date_to: dateTo,
    histogram_bins: bins,
  });
}

export async function analyticsPriceTrend({ code, country, dateFrom, dateTo }) {
  return request('/api/v2/analytics/price_trend', {
    code, country, date_from: dateFrom, date_to: dateTo,
  });
}

export async function analyticsPriceCheck({ code, pricePerKg, country, dateFrom, dateTo }) {
  return request('/api/v2/analytics/price_check', {
    code, price_per_kg: pricePerKg, country,
    date_from: dateFrom, date_to: dateTo,
  });
}

export async function analyticsTopImporters({ code, limit = 20, orderBy = 'value_total_usd' }) {
  return request('/api/v2/analytics/top_importers', {
    code, limit, order_by: orderBy,
  });
}

export async function analyticsCountryBreakdown({ code, dateFrom, dateTo, limit = 30 }) {
  return request('/api/v2/analytics/country_breakdown', {
    code, date_from: dateFrom, date_to: dateTo, limit,
  });
}

export async function analyticsValuationMethodStats(code) {
  return request('/api/v2/analytics/valuation_method_stats', { code });
}

export async function analyticsProductSummary(code) {
  return request('/api/v2/analytics/product_summary', { code });
}

// Семантическая ценовая аналитика — найти похожие товары и посмотреть статистику цен.
// Anchor: либо anchorId (точная запись из Qdrant), либо query (текст).
// Drill-down: priceMin + priceMax — звужує агрегацію до цінового коридору
// (наприклад, бар з гістограми) без зміни якоря.
export async function analyticsSemanticPrice({
  anchorId, query, codePrefix, country, dateFrom, dateTo,
  minSimilarity = 0.6, maxNeighbors = 1000, histogramBins = 20,
  priceMin, priceMax,
}) {
  return request('/api/v2/analytics/semantic_price', {
    anchor_id: anchorId,
    query,
    code_prefix: codePrefix,
    country,
    date_from: dateFrom,
    date_to: dateTo,
    min_similarity: minSimilarity,
    max_neighbors: maxNeighbors,
    histogram_bins: histogramBins,
    price_min: priceMin,
    price_max: priceMax,
  });
}

export async function analyticsDeclarationPractice({
  anchorId, query, codePrefix, country, trademark, dateFrom, dateTo,
  yearFrom, yearTo, minSimilarity, maxCandidates, scope = 'balanced',
  includeSamples = true, includeUndated = true,
}) {
  return request('/api/v2/analytics/declaration_practice', {
    anchor_id: anchorId,
    query,
    code_prefix: codePrefix,
    country,
    trademark,
    date_from: dateFrom,
    date_to: dateTo,
    year_from: yearFrom,
    year_to: yearTo,
    min_similarity: minSimilarity,
    max_candidates: maxCandidates,
    scope,
    include_samples: includeSamples,
    include_undated: includeUndated,
  });
}
